import { FC } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { useSelector } from "react-redux";
import { Progress } from "antd";
import { getTheme } from "Store/MultiDomain/MultiDomain.selector";
import { IUserInforResponse } from "Data/interfaces/Auth/IUserInfoResponse";
import { ITenantData } from "Data/interfaces/Tenant/ITenantData";
import { IOpenFileAction } from "Pages/ListagemArquivos";
import { handleOpenForgeViewer } from "Pages/ListagemArquivos/ListagemArquivos.helpers";
import Modal from "Components/UI/Modal";
import Button from "Components/UI/Button";
import styles from "./ProgressRenderBar.module.scss";

interface IProgressRenderBar {
  maxLimits?: number;
  value: number;
  visible?: boolean;
  role?: number;
  openFileAction?: IOpenFileAction;
  inFederatedViewer?: boolean;
  userInfo?: IUserInforResponse;
  currentTenant?: ITenantData | null;
  tenantIds?: number[];
  disciplineName?: string | null;
  onCancel: () => void;
  onConfirmRender?: () => void;
}

const ProgressRenderBar: FC<IProgressRenderBar> = ({
  maxLimits,
  value,
  visible,
  role,
  openFileAction,
  inFederatedViewer,
  userInfo,
  currentTenant,
  tenantIds,
  disciplineName,
  onCancel,
  onConfirmRender
}) => {
  const { t } = useTranslation('listagemArquivos');
  const navigate = useNavigate();
  const { colors } = useSelector(getTheme);

  const percentOfConstructionsSites = (currentValue: number | undefined, currentLimit: number) => {
    if (currentValue) {
      const value = (currentValue / currentLimit) * 100;
      return Math.floor(value);
    }
  }

  const handleOpenFile = () => {
    if (openFileAction) {
      handleOpenForgeViewer({
        csId: openFileAction?.csId,
        folderId: openFileAction?.record.FileInfo.ApiFolderId ?? '',
        ConstructionSiteDisciplinesId: openFileAction?.ConstructionSiteDisciplinesId,
        record: openFileAction?.record,
        breadCrumbId: openFileAction?.breadCrumbId,
        constructionSiteApiId: openFileAction?.constructionSiteApiId,
        mouseClickEvent: openFileAction?.event,
        userInfo,
        currentTenant,
        tenantIds,
        disciplineName,
        dispatch: openFileAction?.dispatch,
        navigate
      })
      onCancel();
    }
    if (!openFileAction && onConfirmRender) {
      onConfirmRender();
      onCancel();
    }
  }

  const renderBarModalButtons = (
    <div className={styles['progress-render-footer-buttons']}>
      <Button
        onClick={onCancel}
        className={styles['progress-render-cancel-button']}
      >
        {t('common:cancel')}
      </Button>
      <Button
        onClick={() => handleOpenFile()}
        type="primary"
        className={styles['progress-render-confirm-button']}
      >
        {inFederatedViewer ? t('renderBar.continue') : t('renderBar.render')}
      </Button>
    </div>
  );

  return (
    <Modal
      title={t('renderBar.title')}
      visible={visible}
      footerButtons={renderBarModalButtons}
      onCancel={onCancel}
      mask={false}
      wrapClassName="ant-modal-mask-render"
    >
      <div className={styles['wrapper']} >
        <div>
          <Progress
            type="circle"
            width={120}
            className={styles['progress-bar']}
            percent={maxLimits
              ? percentOfConstructionsSites(value, maxLimits)
              : 100
            }
            strokeWidth={7}
            strokeColor={colors.primary.primary}
            format={() => (
              <div>
                <div className={styles['progress-circle-resources']}>
                  <span>{value}</span>/{maxLimits}
                </div>
                <div className={styles['progress-circle-text']}>
                  {t('renderBar.rendered')}
                </div>
              </div>
            )}
          />
        </div>

        <div className={styles['progress-text']}>
          <p>{role === 1
            ? t('renderBar.yourAccountLimit', { count: maxLimits && (maxLimits - value) })
            : t('renderBar.companyAccountLimit', { count: maxLimits && (maxLimits - value) })
          }</p>
          <p>{t('renderBar.confirmRender')}</p>
        </div>
      </div>
    </Modal>
  )
}

export default ProgressRenderBar;
